(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const initShareClub = (scope = document) => {
    scope.querySelectorAll('[data-share-club]').forEach((club) => {
      if (club.dataset.goInit === 'true') return;
      club.dataset.goInit = 'true';
      const stage = club.querySelector('[data-share-stage]');
      const tabs = [...club.querySelectorAll('[data-share-choice]')];
      const panels = [...club.querySelectorAll('[data-share-panel]')];
      const activate = (id) => {
        tabs.forEach((tab) => {
          const active = tab.dataset.shareChoice === id;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.setAttribute('tabindex', active ? '0' : '-1');
        });
        panels.forEach((panel) => {
          const active = panel.dataset.sharePanel === id;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
          if (active && stage) stage.dataset.tone = panel.dataset.tone || 'blue';
        });
        if (stage && !reducedMotion) {
          stage.classList.remove('go-share-pop');
          void stage.offsetWidth;
          stage.classList.add('go-share-pop');
        }
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab.dataset.shareChoice));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(event.key)) return;
          event.preventDefault();
          const delta = ['ArrowRight','ArrowDown'].includes(event.key) ? 1 : -1;
          const next = tabs[(index + delta + tabs.length) % tabs.length];
          next.focus(); activate(next.dataset.shareChoice);
        });
      });
      if (tabs[0]) activate(tabs[0].dataset.shareChoice);
    });
  };

  const initReveal = (scope = document) => {
    const selectors = [
      '.go-manifesto__copy', '.go-manifesto__sticker',
      '.go-section-head', '.go-category-card',
      '.go-sensory__intro', '.go-sensory__point',
      '.go-featured-product__media', '.go-featured-product__copy',
      '.go-sharing-club__head', '.go-sharing-club__tabs', '.go-sharing-club__stage',
      '.go-product-card', '.go-pour-card',
      '.go-craving-loop__top', '.go-craving-step',
      '.go-wholesale__copy', '.go-wholesale__points > *',
      '.go-story__copy', '.go-story__media',
      '.go-newsletter__inner'
    ];
    const nodes = [...scope.querySelectorAll(selectors.join(','))].filter((el) => !el.classList.contains('go-reveal'));
    nodes.forEach((el, index) => {
      el.classList.add('go-reveal');
      if (el.matches('.go-category-card,.go-product-card,.go-pour-card,.go-featured-product__media,.go-sharing-club__stage,.go-story__media')) el.classList.add('go-reveal--scale');
      const groupParent = el.parentElement;
      const siblings = groupParent ? [...groupParent.children].filter((child) => nodes.includes(child)) : [];
      const position = Math.max(0, siblings.indexOf(el));
      el.style.setProperty('--go-delay', `${Math.min(position * 70, 280)}ms`);
    });

    if (reducedMotion || !('IntersectionObserver' in window)) {
      nodes.forEach((el) => el.classList.add('go-inview'));
      return;
    }
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('go-inview');
        obs.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    nodes.forEach((el) => observer.observe(el));
  };

  const initHeroParallax = () => {
    if (reducedMotion) return;
    const hero = document.querySelector('.go-hero--cinematic');
    const image = hero?.querySelector('.go-hero__media img');
    if (!hero || !image) return;
    let ticking = false;
    const update = () => {
      const rect = hero.getBoundingClientRect();
      const viewport = window.innerHeight || 800;
      if (rect.bottom > 0 && rect.top < viewport) {
        const progress = Math.max(-1, Math.min(1, (viewport * .5 - (rect.top + rect.height * .5)) / viewport));
        const amount = window.innerWidth < 750 ? 8 : 18;
        const scale = window.innerWidth < 750 ? 1.02 : 1.035;
        image.style.transform = `translate3d(0, ${progress * amount}px, 0) scale(${scale})`;
      }
      ticking = false;
    };
    const request = () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    };
    update();
    window.addEventListener('scroll', request, { passive:true });
    window.addEventListener('resize', request, { passive:true });
  };

  const initTilt = (scope = document) => {
    if (!finePointer || reducedMotion) return;
    scope.querySelectorAll('.go-category-card,.go-product-card,.go-pour-card').forEach((card) => {
      if (card.dataset.goTilt === 'true') return;
      card.dataset.goTilt = 'true';
      card.classList.add('go-tilt');
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - .5;
        const y = (event.clientY - rect.top) / rect.height - .5;
        card.classList.remove('go-tilt-reset');
        card.style.setProperty('--go-tilt-x', `${(-y * 2.2).toFixed(2)}deg`);
        card.style.setProperty('--go-tilt-y', `${(x * 2.8).toFixed(2)}deg`);
      });
      card.addEventListener('pointerleave', () => {
        card.classList.add('go-tilt-reset');
        card.style.setProperty('--go-tilt-x', '0deg');
        card.style.setProperty('--go-tilt-y', '0deg');
      });
    });
  };

  const initMotion = (scope = document) => {
    document.documentElement.classList.add('go-motion-ready');
    initShareClub(scope);
    initReveal(scope);
    initTilt(scope);
    requestAnimationFrame(() => document.documentElement.classList.add('go-page-entered'));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initMotion();
      initHeroParallax();
    }, { once:true });
  } else {
    initMotion();
    initHeroParallax();
  }

  /* Shopify theme editor can hot-reload individual sections. */
  document.addEventListener('shopify:section:load', (event) => initMotion(event.target));
})();
